/**
 * Z-45: the three eye-tracking calibration protocols, shared by the study
 * wizard (which one to offer/pick) and the respondent journey (which points
 * and head poses to actually run).
 *
 * The list and its shapes are deliberately hard-coded here rather than fetched
 * from anywhere — the gaze container does not (and per the 2026-09-09 decision
 * never will) advertise "which calibrations are available"; that would need it
 * running before a study even exists. See
 * FaceWise-brain/20-produkt/warianty-kalibracji.md.
 *
 * Point counts and timings mirror that note, whose own status is `draft` as of
 * 2026-09-12 (pending confirmation at Z-14) — see the TODOs below for the
 * specific numbers this file had to pick.
 */
import type { CalibrationVariant } from '@/services/projectTypes'

export type { CalibrationVariant }

/** Head pose the respondent is asked to hold while a batch of points runs.
 * Matches FaceWise-brain's "środek/lewo/prawo/góra/dół" (±15° yaw/pitch,
 * respondent-performed — nothing measures or enforces the actual angle). */
export type HeadPose = 'center' | 'left' | 'right' | 'top' | 'bottom'

export interface CalibrationTarget {
  /** Normalised position (0..1) inside the stimulus content rectangle. */
  x: number
  y: number
}

export const CALIBRATION_VARIANTS: CalibrationVariant[] = ['quick-4x1', 'standard-9x3', 'full-9x5']

/** Default variant offered in the wizard (the brain note marks this one
 * "domyślny"). */
export const DEFAULT_CALIBRATION_VARIANT: CalibrationVariant = 'standard-9x3'

// "4 skrajne" — the four corners of the wide grid below.
const CORNERS_4: CalibrationTarget[] = [
  { x: 0.08, y: 0.08 },
  { x: 0.92, y: 0.08 },
  { x: 0.92, y: 0.92 },
  { x: 0.08, y: 0.92 },
]

// "9 szeroko" — a 3x3 grid at the documented fractions (0.08 / 0.5 / 0.92),
// in raster order.
const WIDE_9: CalibrationTarget[] = [
  { x: 0.08, y: 0.08 }, { x: 0.5, y: 0.08 }, { x: 0.92, y: 0.08 },
  { x: 0.08, y: 0.5 }, { x: 0.5, y: 0.5 }, { x: 0.92, y: 0.5 },
  { x: 0.08, y: 0.92 }, { x: 0.5, y: 0.92 }, { x: 0.92, y: 0.92 },
]

// Validation targets — the pass that turns "orientacyjny" into a number the
// researcher can read (protokol-gaze-kontener.md: obowiązkowa, nie opcja).
//
// Deliberately NOT the calibration grid. Two reasons: a model scored on its own
// training points reports how well it memorised, not how well it generalises;
// and Z-15 says today's validation sits too close to the centre, so it ends up
// measuring head pose rather than gaze. Hence 0.15/0.85 — near the edges, but
// inset from the 0.08/0.92 calibration corners so the score is interpolation.
//
// TODO (Z-14/Z-15): the counts come from warianty-kalibracji.md (status draft);
// the acceptable error threshold is still open (candidate 3°).
const VALIDATION_9: CalibrationTarget[] = [
  { x: 0.15, y: 0.15 }, { x: 0.5, y: 0.15 }, { x: 0.85, y: 0.15 },
  { x: 0.15, y: 0.5 }, { x: 0.5, y: 0.5 }, { x: 0.85, y: 0.5 },
  { x: 0.15, y: 0.85 }, { x: 0.5, y: 0.85 }, { x: 0.85, y: 0.85 },
]

// The short variant calibrates on the four corners only, so its validation
// probes what the corners say least about: the middle and the two edge centres.
const VALIDATION_3: CalibrationTarget[] = [
  { x: 0.5, y: 0.5 },
  { x: 0.15, y: 0.5 },
  { x: 0.85, y: 0.5 },
]

export interface CalibrationVariantShape {
  id: CalibrationVariant
  /** Target points, shown in full at every head pose below. */
  points: CalibrationTarget[]
  /** Head poses the respondent is walked through, in order. */
  poses: HeadPose[]
  /**
   * Points shown in the validation pass that closes every variant, always in
   * the "center" head pose. The gaze engine fits on `points` and scores itself
   * on these, which is the only thing that produces `quality` — so this is
   * mandatory, not an option, and an empty list would make the engine refuse
   * the whole session (`no_validation_points` -> 422).
   */
  validationTargets: CalibrationTarget[]
  /** Rough end-to-end duration in seconds, as documented in
   * warianty-kalibracji.md (status: draft, not derived to match the timing
   * constants below exactly — see the note there on why the two don't line
   * up to the second). Used only for the wizard's "~X minut" copy. */
  approxSeconds: number
}

export const CALIBRATION_VARIANT_SHAPES: Record<CalibrationVariant, CalibrationVariantShape> = {
  'quick-4x1': {
    id: 'quick-4x1',
    points: CORNERS_4,
    poses: ['center'],
    validationTargets: VALIDATION_3,
    approxSeconds: 25,
  },
  'standard-9x3': {
    id: 'standard-9x3',
    points: WIDE_9,
    poses: ['center', 'left', 'right'],
    validationTargets: VALIDATION_9,
    approxSeconds: 90,
  },
  'full-9x5': {
    id: 'full-9x5',
    points: WIDE_9,
    poses: ['center', 'left', 'right', 'top', 'bottom'],
    validationTargets: VALIDATION_9,
    approxSeconds: 150,
  },
}

/** Number of head poses in a variant — the one fact the wizard card needs
 * beyond the point count and the approximate duration. */
export function poseCount(variant: CalibrationVariant): number {
  return CALIBRATION_VARIANT_SHAPES[variant].poses.length
}
